import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Circle as CircleIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemeVariant } from '../../theme';

interface ThemeOption {
  value: ThemeVariant;
  label: string;
  color: string;
  description: string;
}

const themeOptions: ThemeOption[] = [
  {
    value: 'heritage',
    label: 'Heritage',
    color: '#8B4513',
    description: 'Warm brown tones perfect for genealogy',
  },
  {
    value: 'blue',
    label: 'Ocean Blue',
    color: '#1565C0',
    description: 'Professional blue tones',
  },
  {
    value: 'green',
    label: 'Forest Green',
    color: '#2E7D32',
    description: 'Natural green tones',
  },
  {
    value: 'orange',
    label: 'Autumn Orange',
    color: '#E65100',
    description: 'Vibrant orange tones',
  },
  {
    value: 'purple',
    label: 'Royal Purple',
    color: '#6A1B9A',
    description: 'Elegant purple tones',
  },
  {
    value: 'christmas',
    label: 'Christmas',
    color: '#C62828',
    description: 'Festive red and green',
  },
];

export const ThemeSelector: React.FC = () => {
  const { variant, setThemeVariant } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeSelect = (themeVariant: ThemeVariant) => {
    setThemeVariant(themeVariant);
    handleClose();
  };

  const currentTheme = themeOptions.find((option) => option.value === variant);

  return (
    <>
      <Tooltip title="Change color theme">
        <IconButton
          onClick={handleClick}
          size="medium"
          aria-label="theme selector"
          aria-controls={open ? 'theme-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            color: 'inherit',
          }}
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="theme-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 280,
            maxWidth: 320,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Color Theme
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Choose your preferred color scheme
          </Typography>
        </Box>
        <Divider />
        {themeOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={variant === option.value}
            onClick={() => handleThemeSelect(option.value)}
            sx={{
              py: 1.5,
              px: 2,
              gap: 1.5,
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <CircleIcon
                sx={{
                  fontSize: '1.25rem',
                  color: option.color,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={option.label}
              secondary={option.description}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: variant === option.value ? 600 : 400,
              }}
              secondaryTypographyProps={{
                variant: 'caption',
              }}
            />
            {variant === option.value && (
              <CheckIcon
                fontSize="small"
                sx={{
                  color: 'primary.main',
                  ml: 'auto',
                }}
              />
            )}
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Current theme: {currentTheme?.label}
          </Typography>
        </Box>
      </Menu>
    </>
  );
};

export default ThemeSelector;
